import React from "react";
import { onBackdropMouseDown, onModalPanelMouseDown } from "../lib/modal";
import { NavLink, useNavigate } from "react-router-dom";
import { useLanguage } from "@/context/LanguageContext";
import { useTheme } from "@/context/ThemeContext";
import BranchSwitcher from "@/components/BranchSwitcher";
import ChangePasswordButton from "@/components/ChangePasswordButton";
import { useAuth } from "@/context/AuthContext";
import { api, setAccessToken } from "@/lib/api";
import {
  LayoutDashboard, CalendarDays, Scissors, Package as PackageIcon, ShoppingBag, Users as UsersIcon,
  Store, Boxes, Wallet, Settings as SettingsIcon, LogOut, Languages, Tag, ShoppingCart, BarChart3,
  ArrowUpDown, Landmark, Truck, UserCog, Building2, Globe, Ticket, Star, Percent, LineChart, Bell,
  MessageCircle, Menu, X, ChevronDown, ChevronUp, Briefcase, IdCard, FileText, ShieldCheck, Sparkles,
  Inbox, Clock, Crown,
} from "lucide-react";

const LOGO = "https://customer-assets.emergentagent.com/job_beauty-hub-manage/artifacts/bqmxhyxp_image.png";
const COLLAPSE_KEY = "isaloon_sidebar_collapsed";

export default function Layout({ children }) {
  const { t, lang, toggleLang } = useLanguage();
  const { theme } = useTheme();
  const { user, logout, canAccess, canAccessPage } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [unread, setUnread] = React.useState(0);
  const [subscription, setSubscription] = React.useState(null);
  // Sidebar can be "fixed" (always expanded) or "auto-hide" (hover-expand).
  // Driven by the tenant's theme settings; falls back to "auto-hide".
  const sidebarFixed = theme?.sidebar_mode === "fixed";
  // Hover-expand sidebar (YouTube-style). Default = collapsed (icons only).
  const [hovered, setHovered] = React.useState(false);
  const expanded = sidebarFixed || hovered;
  const [collapsed, setCollapsed] = React.useState(() => {
    try { return JSON.parse(localStorage.getItem(COLLAPSE_KEY) || "{}"); } catch { return {}; }
  });

  React.useEffect(() => {
    try { localStorage.setItem(COLLAPSE_KEY, JSON.stringify(collapsed)); } catch {}
  }, [collapsed]);

  React.useEffect(() => {
    let active = true;
    const fetchUnread = async () => {
      try {
        const { api } = await import("@/lib/api");
        const r = await api.get("/notifications?unread_only=true");
        if (active) setUnread(r.data.length);
      } catch {}
    };
    fetchUnread();
    const tm = setInterval(fetchUnread, 30000);
    return () => { active = false; clearInterval(tm); };
  }, []);

  // Subscription summary for Topbar pill. Tenant-scoped server-side.
  // Refreshed every 5 minutes so plan/status changes propagate without a
  // full page reload.
  React.useEffect(() => {
    let active = true;
    const fetchSub = async () => {
      try {
        const { api } = await import("@/lib/api");
        const r = await api.get("/me/subscription");
        if (active) setSubscription(r.data);
      } catch {
        if (active) setSubscription(null);
      }
    };
    fetchSub();
    const tm = setInterval(fetchSub, 5 * 60 * 1000);
    return () => { active = false; clearInterval(tm); };
  }, []);

  // TOP-LEVEL (always visible, not grouped)
  const topItems = [
    { to: "/pos", label: t("nav_pos"), icon: Store, id: "pos" },
    { to: "/bookings", label: t("nav_bookings"), icon: CalendarDays, id: "bookings" },
  ];

  // GROUPED SECTIONS
  const sections = [
    {
      id: "overview",
      title: lang === "ar" ? "نظرة عامة" : "Overview",
      items: [
        { to: "/", label: t("nav_dashboard"), icon: LayoutDashboard, id: "dashboard" },
        { to: "/customers", label: t("nav_customers"), icon: UsersIcon, id: "customers" },
      ],
    },
    {
      id: "catalog",
      title: lang === "ar" ? "الكتالوج" : "Catalog",
      items: [
        { to: "/services", label: t("nav_services"), icon: Scissors, id: "services" },
        { to: "/packages", label: t("nav_packages"), icon: PackageIcon, id: "packages" },
        { to: "/products", label: t("nav_products"), icon: ShoppingBag, id: "products" },
        { to: "/categories", label: t("nav_categories"), icon: Tag, id: "categories" },
      ],
    },
    {
      id: "inventory",
      title: lang === "ar" ? "المخزون والمشتريات" : "Inventory & Purchases",
      items: [
        { to: "/purchases", label: t("nav_purchases"), icon: ShoppingCart, id: "purchases" },
        { to: "/suppliers", label: t("nav_suppliers"), icon: Truck, id: "suppliers" },
        { to: "/inventory", label: t("nav_inventory"), icon: Boxes, id: "inventory" },
        { to: "/stock-movements", label: t("nav_stock_movements"), icon: ArrowUpDown, id: "stock-movements" },
        { to: "/inventory-transfers", label: lang === "ar" ? "تحويلات الفروع" : "Branch Transfers", icon: ArrowUpDown, id: "stock-movements", sub: true },
      ],
    },
    {
      id: "finance",
      title: lang === "ar" ? "المالية" : "Finance",
      items: [
        { to: "/accounting", label: t("nav_accounting"), icon: Wallet, id: "accounting" },
        { to: "/accounts", label: t("nav_accounts"), icon: Landmark, id: "accounts" },
        { to: "/invoice-design", label: lang === "ar" ? "هوية الطباعة" : "Print Identity", icon: FileText, id: "invoice-design" },
        { to: "/reports", label: t("nav_reports"), icon: BarChart3, id: "reports" },
        { to: "/commissions", label: t("nav_commissions"), icon: Percent, id: "commissions" },
        { to: "/staff-payouts", label: t("nav_staff_payouts"), icon: Wallet, id: "commissions" },
      ],
    },
    {
      id: "hr",
      title: lang === "ar" ? "الموارد البشرية" : "Human Resources",
      items: [
        { to: "/employees", label: lang === "ar" ? "الموظفون" : "Employees", icon: Briefcase, id: "employees" },
        { to: "/staff-rota", label: lang === "ar" ? "جدول الدوام" : "Staff Rota", icon: Clock, id: "employees" },
        { to: "/payroll", label: lang === "ar" ? "الرواتب" : "Payroll", icon: Wallet, id: "payroll" },
        // Attendance module hidden — kept for future refactor. Route still exists
        // and a user with the direct URL can reach the page, but it no longer
        // appears in the sidebar so the salon doesn't see it day-to-day.
        // { to: "/attendance", label: lang === "ar" ? "الحضور" : "Attendance", icon: Clock, id: "attendance" },
        { to: "/departments", label: lang === "ar" ? "الأقسام" : "Departments", icon: Building2, id: "departments" },
        { to: "/users", label: t("nav_users"), icon: UserCog, id: "users" },
      ],
    },
    {
      id: "marketing",
      title: lang === "ar" ? "النمو والتسويق" : "Growth & Marketing",
      items: [
        { to: "/website", label: t("nav_website"), icon: Globe, id: "website" },
        { to: "/coupons", label: t("nav_coupons"), icon: Ticket, id: "coupons" },
        { to: "/reviews", label: t("nav_reviews"), icon: Star, id: "reviews" },
        { to: "/analytics", label: t("nav_analytics"), icon: LineChart, id: "analytics" },
        // WhatsApp Campaigns — broadcast to customer segments.
        // (WhatsApp *settings* live under System → Settings.)
        { to: "/whatsapp", label: lang === "ar" ? "حملات واتساب" : "WhatsApp Campaigns", icon: MessageCircle, id: "whatsapp" },
      ],
    },
    {
      id: "system",
      title: lang === "ar" ? "النظام" : "System",
      items: [
        { to: "/notifications", label: t("nav_notifications"), icon: Bell, id: "notifications", badge: unread },
        { to: "/branches", label: t("nav_branches"), icon: IdCard, id: "branches" },
        // Public marketplace listing — each tenant manages their OWN card,
        // photos, branch visibility. Lives under System (not Platform) so
        // any tenant owner / manager can reach it.
        { to: "/marketplace/listing", label: lang === "ar" ? "بطاقة المتجر العام" : "Marketplace Listing", icon: Store, id: "marketplace-listing" },
        { to: "/settings", label: t("nav_settings"), icon: SettingsIcon, id: "settings" },
      ],
    },
    ...(user?.role === "admin" ? [{
      id: "platform",
      title: lang === "ar" ? "المنصة" : "Platform",
      items: [
        { to: "/platform-admin", label: lang === "ar" ? "إدارة المنصة" : "Platform Admin", icon: Building2, id: "platform-admin", adminOnly: true },
        { to: "/platform/marketplace", label: lang === "ar" ? "Listings (المنصة)" : "Listings (Platform)", icon: Store, id: "platform-marketplace", adminOnly: true },
        { to: "/platform/tenancy", label: lang === "ar" ? "إدارة Tenancy" : "Tenancy Admin", icon: ShieldCheck, id: "platform-tenancy", adminOnly: true },
        { to: "/platform/onboarding", label: lang === "ar" ? "Onboarding صالون" : "Onboarding Wizard", icon: Sparkles, id: "platform-onboarding", adminOnly: true },
        // WhatsApp (Platform) removed — each tenant manages their own settings now
        // under System → Settings → WhatsApp tab.
        { to: "/platform/reviews", label: lang === "ar" ? "إشراف التقييمات" : "Reviews moderation", icon: Star, id: "platform-reviews", adminOnly: true },
        { to: "/platform/tenants/pending", label: lang === "ar" ? "صندوق طلبات التسجيل" : "Tenant signups inbox", icon: Inbox, id: "platform-tenants-pending", adminOnly: true },
      ],
    }] : []),
  ];

  // Visibility test — combines user-level RBAC (allowed_modules) with the
  // tenant's plan-level page gate. Both must permit; same Plan ∩ User logic
  // the backend uses for API enforcement, so what's hidden in the sidebar
  // is exactly what the API also blocks.
  const isAllowed = (item) => {
    if (item.adminOnly) return user?.role === "admin";
    if (!canAccess(item.id)) return false;
    return canAccessPage(item.id);
  };

  const toggleSection = (id) => setCollapsed((c) => ({ ...c, [id]: !c[id] }));

  const renderTopItem = (n, closeMenu) => (
    <NavLink
      key={n.to}
      to={n.to}
      end={n.to === "/"}
      data-testid={`nav-${n.id}`}
      data-top-item
      onClick={closeMenu}
      className={({ isActive }) =>
        `flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm font-semibold transition ${
          isActive
            ? "bg-white text-[var(--gold)] font-semibold shadow-sm"
            : "text-[var(--sidebar-text)] hover:bg-[var(--sidebar-hover)]"
        }`
      }
    >
      <n.icon size={18} />
      <span className="flex-1">{n.label}</span>
      {n.badge > 0 && <span className="text-xs px-1.5 py-0.5 rounded-full bg-rose-500 text-white">{n.badge}</span>}
    </NavLink>
  );

  const renderSection = (section, closeMenu) => {
    const visible = section.items.filter(isAllowed);
    if (!visible.length) return null;
    const isCollapsed = !!collapsed[section.id];
    return (
      <div key={section.id} className="mb-2" data-section-block={section.id}>
        <button
          onClick={() => toggleSection(section.id)}
          data-testid={`section-toggle-${section.id}`}
          data-section-toggle
          className="w-full flex items-center justify-between px-3 py-1.5 text-[10px] uppercase tracking-wider text-[var(--text-muted)] font-semibold hover:text-[var(--text-strong)] transition"
        >
          <span data-section-header>{section.title}</span>
          {isCollapsed ? <ChevronDown size={12} /> : <ChevronUp size={12} />}
        </button>
        {!isCollapsed && (
          <div className="space-y-0.5 mt-0.5">
            {visible.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === "/"}
                data-testid={`nav-${n.id}`}
                onClick={closeMenu}
                className={({ isActive }) => `sidebar-item ${isActive ? "active" : ""}`}
              >
                <n.icon size={16} />
                <span className="flex-1">{n.label}</span>
                {n.badge > 0 && <span className="text-xs px-1.5 py-0.5 rounded-full bg-rose-500 text-white">{n.badge}</span>}
              </NavLink>
            ))}
          </div>
        )}
      </div>
    );
  };

  const NavContent = ({ closeMenu }) => (
    <>
      {/* Top pinned items */}
      <div className="space-y-1 mb-4 pb-3 border-b border-[var(--border)]">
        {topItems.filter(isAllowed).map((n) => renderTopItem(n, closeMenu))}
      </div>
      {sections.map((s) => renderSection(s, closeMenu))}
    </>
  );

  return (
    <div className="min-h-screen flex" data-testid="app-layout">
      {/* Desktop Sidebar — supports both "fixed" (always expanded) and "auto-hide" (hover-expand) modes via theme settings. */}
      <aside
        onMouseEnter={() => !sidebarFixed && setHovered(true)}
        onMouseLeave={() => !sidebarFixed && setHovered(false)}
        data-expanded={expanded ? "true" : "false"}
        data-sidebar-mode={sidebarFixed ? "fixed" : "auto-hide"}
        data-testid="sidebar"
        className={`hidden md:flex flex-col border-e border-[var(--border)] bg-[var(--sidebar)] sticky top-0 h-screen overflow-hidden transition-[width] duration-200 ease-out ${expanded ? "w-64 shadow-xl z-40" : "w-[68px]"}`}
      >
        <div className="p-3 flex items-center justify-center border-b border-white/10 min-h-[120px]">
          <div className={`rounded-2xl bg-white shadow-lg flex items-center justify-center transition-all duration-200 ${expanded ? "w-full h-24 p-3" : "w-14 h-14 p-1"}`}>
            <img
              src={LOGO}
              alt="iSaloon by Maham"
              className="max-w-full max-h-full object-contain"
              data-testid="sidebar-logo"
            />
          </div>
        </div>
        <nav className={`flex-1 p-2 overflow-y-auto overflow-x-hidden sidebar-rail ${expanded ? "sidebar-rail-open" : ""}`}>
          <NavContent closeMenu={() => {}} />
        </nav>
        <div className="p-2 border-t border-white/10">
          <button onClick={logout} data-testid="logout-button"
            className="sidebar-item w-full justify-start whitespace-nowrap">
            <LogOut size={18} /><span className={`transition-opacity ${expanded ? "opacity-100" : "opacity-0"}`}>{t("logout")}</span>
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-16 border-b border-white/10 bg-[var(--sidebar)] flex items-center justify-between px-4 md:px-8 sticky top-0 z-30">
          <div className="flex items-center gap-3 md:hidden">
            <button onClick={() => setMobileOpen(true)} data-testid="mobile-menu-toggle" className="p-2 rounded-md hover:bg-white/10 text-white"><Menu size={20} /></button>
            <img src={LOGO} alt="iSaloon by Maham" className="h-12 object-contain bg-white rounded-lg p-1" />
          </div>
          <div className="hidden md:block text-sm text-white font-medium">{user?.email}</div>
          <div className="flex items-center gap-2">
            <SubscriptionBadge sub={subscription} lang={lang} />
            <BranchSwitcher />
            <NavLink to="/notifications" className="relative p-2 rounded-md hover:bg-white/10 text-white hidden sm:block" data-testid="notification-bell">
              <Bell size={18} />
              {unread > 0 && <span className="absolute top-1 end-1 w-2 h-2 rounded-full bg-amber-300"></span>}
            </NavLink>
            <ChangePasswordButton />
            <button onClick={toggleLang} data-testid="language-toggle" className="flex items-center gap-2 px-3 py-2 rounded-md border border-white/30 hover:border-white text-sm text-white transition">
              <Languages size={16} /><span>{lang === "ar" ? "EN" : "ع"}</span>
            </button>
          </div>
        </header>

        {mobileOpen && (
          <div className="md:hidden fixed inset-0 z-50 flex" onMouseDown={(e) => onBackdropMouseDown(e, () => setMobileOpen(false))}>
            <div className="absolute inset-0 bg-black/40" />
            <aside className="relative w-72 max-w-[85vw] bg-[var(--sidebar)] border-e border-[var(--border)] flex flex-col" onMouseDown={onModalPanelMouseDown}>
              <div className="p-4 border-b border-white/10 flex items-center justify-between">
                <img src={LOGO} alt="iSaloon by Maham" className="h-16 object-contain bg-white rounded-xl p-2" />
                <button onClick={() => setMobileOpen(false)} className="text-white"><X size={20} /></button>
              </div>
              <nav className="flex-1 p-3 overflow-auto">
                <NavContent closeMenu={() => setMobileOpen(false)} />
                <button onClick={logout} className="sidebar-item w-full mt-2"><LogOut size={18} /><span>{t("logout")}</span></button>
              </nav>
            </aside>
          </div>
        )}

        <main className="flex-1 p-4 md:p-8 overflow-auto">
          <ImpersonationBanner />
          {children}
        </main>
      </div>
    </div>
  );
}

// Topbar subscription badge — shows live status (Active/Trial/Suspended/
// Expired) and the expiry date next to the user profile. Driven by
// `/api/me/subscription`. Tenant-scoped server-side, so platform admins
// (no tenant) get nothing rendered.
function SubscriptionBadge({ sub, lang }) {
  if (!sub || !sub.plan) return null;
  const isAr = lang === "ar";
  const statusToneEn = {
    active:    { bg: "bg-emerald-50", border: "border-emerald-200", text: "text-emerald-700", dot: "bg-emerald-500", label: { ar: "نشط", en: "Active" } },
    trial:     { bg: "bg-sky-50",     border: "border-sky-200",     text: "text-sky-700",     dot: "bg-sky-500",     label: { ar: "تجريبي", en: "Trial" } },
    suspended: { bg: "bg-rose-50",    border: "border-rose-200",    text: "text-rose-700",    dot: "bg-rose-500",    label: { ar: "موقوف", en: "Suspended" } },
    expired:   { bg: "bg-stone-50",   border: "border-stone-200",   text: "text-stone-600",   dot: "bg-stone-400",   label: { ar: "منتهي", en: "Expired" } },
  };
  const tone = statusToneEn[sub.status] || statusToneEn.active;
  const fmtDate = (iso) => {
    if (!iso) return isAr ? "—" : "—";
    try {
      const d = new Date(iso);
      return d.toLocaleDateString(isAr ? "ar-QA" : "en-GB", { year: "numeric", month: "short", day: "2-digit" });
    } catch { return iso; }
  };
  const expiry = fmtDate(sub.status === "trial" && sub.trial_ends_at ? sub.trial_ends_at : sub.subscription_ends_at);
  return (
    <div
      className="hidden md:flex items-center gap-2 px-2.5 py-1.5 rounded-lg border border-white/30 bg-white/15 backdrop-blur-sm"
      data-testid="topbar-subscription-badge"
      title={isAr ? `${tone.label.ar} · ينتهي ${expiry}` : `${tone.label.en} · expires ${expiry}`}
    >
      <span
        className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-semibold border ${tone.bg} ${tone.border} ${tone.text}`}
        data-testid="topbar-sub-status"
      >
        <span className={`w-1.5 h-1.5 rounded-full ${tone.dot}`} />
        {tone.label[lang] || tone.label.en}
      </span>
      <span className="flex items-center gap-1 text-[11px] font-medium num-display text-white" data-testid="topbar-sub-expiry">
        <Clock size={11} className="text-white/70" />
        <span>{expiry}</span>
      </span>
    </div>
  );
}

// Renders only when the operator has impersonated a tenant owner from the
// platform-admin shell. Keeps the visual loud (amber + sticky) so they don't
// forget to exit before doing risky writes. Click → log out → operator can
// re-login as admin to return.
function ImpersonationBanner() {
  const { user, checkAuth } = useAuth();
  const isImpersonating = user?.impersonated;
  const meta = React.useMemo(() => {
    try { return JSON.parse(localStorage.getItem("impersonating") || "null"); }
    catch { return null; }
  }, [isImpersonating]);
  if (!isImpersonating) return null;
  const exit = async () => {
    try {
      const { data } = await api.post("/auth/exit-impersonation");
      if (data?.access_token) setAccessToken(data.access_token);
      localStorage.removeItem("impersonating");
      await checkAuth();
      window.location.replace("/platform-admin/tenants");
    } catch {
      // Fallback safety: if restoration fails, force a clean login.
      localStorage.removeItem("impersonating");
      setAccessToken(null);
      window.location.replace("/login");
    }
  };
  return (
    <div className="mb-4 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 flex items-center justify-between gap-3 text-sm shadow-sm" data-testid="impersonation-banner">
      <div className="flex items-center gap-2 text-amber-900">
        <ShieldCheck size={16}/>
        <span className="font-semibold">Impersonating:</span>
        <span>{meta?.tenant_name || meta?.owner_username || user?.username}</span>
        <span className="opacity-70">· session ends in ≤ {meta?.expires_in_minutes || 120} min</span>
      </div>
      <button onClick={exit} className="px-3 py-1.5 rounded-lg bg-amber-600 text-white text-xs font-semibold hover:bg-amber-700" data-testid="impersonation-exit">
        Exit impersonation
      </button>
    </div>
  );
}
